<script lang="ts" setup>
    const props = withDefaults(defineProps<{
        modelValue: string | number
        inputValue: string | number
        name: string
        type?: 'primary' | 'secondary'
    }>(), { type: 'primary' });

    const nameModel = ref(props.modelValue ?? '');

    const emit = defineEmits<{(event: 'update:modelValue', value: string | number)}>();

    const handleUpdateModel = (): void => {
        nameModel.value = props.inputValue;
    };

    watch(() => nameModel.value, (nVal: string | number) => {
        emit('update:modelValue', nVal);
    });

    watch(() => props.modelValue, (nVal: string | number) => {
        if (nVal) nameModel.value = nVal;
    });
</script>

<template>
    <label
        :class="{
            'active': inputValue === nameModel,
            'secondary': type === 'secondary'
        }"
        class="widget-radio-input"
        @click="handleUpdateModel"
    >
        <input
            v-model="nameModel"
            type="radio"
            :name="name"
            :value="inputValue"
        >
        <div class="widget-radio-input__label">
            <slot />
        </div>
    </label>
</template>

<style scoped lang="scss">
    .widget-radio-input {
        display: flex;
        align-items: center;
        cursor: pointer;
        gap: var(--spacing-03);

        @include min-width(md) {
            gap: var(--spacing-04);
        }

        &::before {
            box-sizing: border-box;
            border: solid var(--spacing-02);
            border-color: var(--background-primary);
            border-radius: 50%;
            background-color: var(--background-primary);
            block-size: var(--size-7);
            content: "";
            inline-size: var(--size-7);
            min-block-size: var(--size-7);
            min-inline-size: var(--size-7);
            transition: all 150ms ease-in-out;

            @include min-width(md) {
                block-size: var(--size-8);
                inline-size: var(--size-8);
                min-block-size: var(--size-8);
                min-inline-size: var(--size-8);
            }
        }

        &:focus-within,
        &:hover,
        &.active {
            &::before {
                background-color: var(--border-primary);
            }
        }

        &.secondary {
            &::before {
                border-color: var(--background-secondary);
                background-color: var(--background-secondary);
            }

            &:focus-within,
            &:hover,
            &.active {
                &::before {
                    background-color: var(--border-primary);
                }
            }
        }

        &__label {
            overflow: hidden;
            flex-grow: 1;
            color: var(--text-primary);
            font-family: var(--font-secondary);
            font-size: var(--font-h2);
            font-weight: var(--font-weight-medium);
            letter-spacing: var(--letter-spacing-sm);
            line-height: var(--line-height-150);
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        input {
            display: none;
        }
    }
</style>
